import React, { useState } from 'react';
import classNames from 'classnames';
import { runInAction } from 'mobx';
import { useLocalStore } from 'mobx-react';
import { useIntl } from 'react-intl';

import { AppConfigKeys, appConfigStore } from 'Models/AppConfigStore';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { PassengerTypeKey } from 'Models/Enums';
import { isNotNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import If from 'Views/Components/If/If';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { FlatPickrDateInput } from 'Views/Components/New/FlatPickrDateInput';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { TextInput } from 'Views/Components/New/TextInput';
import { PhoneInput } from 'Views/Components/New/PhoneInput';
import {
	BookingWizardData,
	getPassengerTickets,
	setPassengerTickets,
} from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { PassengerDetail } from '../../BookingWizardData/PassengerDetail';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { PassengerError, passengerTicketIsEmpty, validatePassenger } from './PassengerTabHelpers';

interface PassengerDetailFormProps {
	forCheckIn: boolean;
	passenger: PassengerDetail;
	count: number;
	expanded: boolean;
	setExpanded: (id: string, clearExpanded?: boolean) => void;
	onClearForm: () => void;
	errors: PassengerError[];
	setErrors: (errors: PassengerError[]) => void;
	showErrors: boolean;
	usingExistingPassenger: boolean;
	resetContinue?: () => void;
	isComplete?: boolean;
	/**
	 * Optional escape hatch for hosts that aren't backed by the booking wizard
	 * store (e.g. check-in form). When provided, edits flow through this
	 * callback instead of writing to `wizardData.innerWizardData`.
	 */
	onPassengerUpdate?: (updated: PassengerDetail) => void;
	/**
	 * Whether the booking carries a vehicle. Passed by hosts whose booking type is not in the wizard store
	 * (check-in), where the store still holds whatever the booking wizard last had open. Left unset, it is
	 * read from the store.
	 */
	isVehicleBooking?: boolean;
}

function formatDateValue(value: Date | string | null | undefined): string | undefined {
	if (!value) return undefined;
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return undefined;
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

export function PassengerDetailForm({
	forCheckIn,
	passenger,
	count,
	expanded,
	setExpanded,
	errors,
	setErrors,
	usingExistingPassenger,
	showErrors,
	onClearForm,
	resetContinue,
	isComplete,
	onPassengerUpdate,
	isVehicleBooking: isVehicleBookingProp,
}: PassengerDetailFormProps) {
	const intl = useIntl();
	const { wizardData, setWizardData } = useBookingWizardDataStore();
	// On mobile the Done/Clear actions move from the card header to a footer beneath the fields, so
	// they're reachable after filling the form rather than scrolled away at the top.
	const { isMobileBookingWizard } = useDeviceDetect();

	const [overrideShowErrors, setOverrideShowErrors] = useState(showErrors);
	const passengersErrors = isNotNullOrUndefined(errors) ? errors?.find(y => y.id === passenger.id)?.errors : {};
	const updateErrors = (_property: keyof PassengerDetail) => {
		if (Object.keys(passengersErrors ?? {}).length === 0) {
			if (resetContinue) {
				runInAction(() => {
					resetContinue();
				});
			}
			return;
		}

		// Get new errors for the passenger
		const newPassengerErrors = validatePassenger(passenger);
		let updatedErrors = [];

		if (newPassengerErrors === null) {
			updatedErrors = [...errors.filter(err => err.id !== passenger.id)];
			if (resetContinue) {
				runInAction(() => {
					resetContinue();
				});
			}
		} else {
			// Update the errors
			updatedErrors = [...errors.filter(err => err.id !== passenger.id), newPassengerErrors];
		}

		setErrors(updatedErrors as PassengerError[]);
	};

	const updatePassengerInfo = (id: string, updatedPassenger: PassengerDetail) => {
		// External-host path: when an `onPassengerUpdate` callback is provided
		// (e.g. check-in form), bypass the wizard store entirely — the host owns
		// the state. Without this hosts have to fight against `setWizardData`
		// being a no-op.
		if (onPassengerUpdate) {
			onPassengerUpdate(updatedPassenger);
			return;
		}

		const newData = new BookingWizardData({ ...wizardData });

		const passengerTypeProperty = passengerTypeStore.getWizardDataProperty(passenger.key as PassengerTypeKey);
		setPassengerTickets(
			newData.innerWizardData,
			passengerTypeProperty,
			(getPassengerTickets(newData.innerWizardData, passengerTypeProperty) ?? []).map(
				(passengerInfo: PassengerDetail) => (passengerInfo.id === id ? updatedPassenger : passengerInfo),
			),
		);

		if (setWizardData) {
			setWizardData(newData as BookingWizardData);
		}
	};

	// Need to ensure the date is a Date object for it to work with the DatePicker
	const dateModel = useLocalStore(() => ({
		dateOfBirth: isNotNullOrUndefined(passenger.dateOfBirth) ? new Date(passenger.dateOfBirth) : null,
	}));

	const handleInputChange = (property: keyof PassengerDetail, value: any) => {
		const updatedPassenger = {
			...passenger,
			[property]: value,
		};
		updatePassengerInfo(passenger.id, updatedPassenger);
		updateErrors(property);
	};

	const showFieldErrors = overrideShowErrors || showErrors;

	// In check-in, the per-field "required" flags are gated by the
	// `Passenger.passengerInfoInCheckIn.required` config — when it's off,
	// no asterisks should show and no fields are required to continue.
	const fieldsRequired = !forCheckIn || appConfigStore.isConfigEnabled(AppConfigKeys.passengerInfoInCheckInRequired);

	// The primary-contact passenger carries the booking's "name on booking", so its first/last name
	// is required whenever that field is configured — even if passenger names are otherwise optional.
	// Mirrors validatePassenger so the required asterisks match the validation.
	const isPrimaryContactPassenger = !!passenger.isPrimaryContact && appConfigStore.collectPrimaryBookingNameEnabled;
	const firstNameRequired =
		(fieldsRequired && appConfigStore.passengerFirstNameRequired) ||
		(isPrimaryContactPassenger && appConfigStore.primaryBookingFirstNameEnabled);
	const lastNameRequired =
		(fieldsRequired && appConfigStore.passengerLastNameRequired) ||
		(isPrimaryContactPassenger && appConfigStore.primaryBookingLastNameEnabled);

	// Driver passenger sync: when the customer/vehicle flow has already populated
	// the driver's first name / last name / phone on the vehicle wizard data,
	// lock those fields here so the user edits them in one place. If a field is
	// missing on the driver record (e.g. customer has no phone on file), leave
	// it editable so the user can fill it in directly on the passenger card.
	const innerData = wizardData.innerWizardData;
	const isVehicleBooking = isVehicleBookingProp ?? isVehicleBookingWizardData(innerData);
	const isDriverPassenger = passenger.driver === true && isVehicleBooking;
	// The locks mirror the wizard's own driver record. A host that passes its booking type keeps its driver
	// elsewhere, and the store's record then belongs to whatever the wizard last had open.
	const driverRecord = isVehicleBookingProp === undefined && isVehicleBookingWizardData(innerData) ? innerData : null;
	const driverFirstNameLocked = isDriverPassenger && stringNotEmpty(driverRecord?.driverFirstName);
	const driverLastNameLocked = isDriverPassenger && stringNotEmpty(driverRecord?.driverLastName);
	const driverPhoneLocked = isDriverPassenger && stringNotEmpty(driverRecord?.driverPhone);

	// When the card is collapsed and the user has triggered validation, surface
	// the error state visually: red border on the card and a red warning icon
	// in place of the green tick / pending dots. While expanded the user is
	// already seeing the per-field errors, so we keep the chrome neutral.
	const hasVisibleErrors = !isComplete && Object.keys(passengersErrors ?? {}).length > 0 && showFieldErrors;
	const showCollapsedErrorState = hasVisibleErrors && !expanded;

	// The expanded-state actions (Clear + Done). Rendered in the header on web and in a footer below
	// the fields on mobile — see isMobileBookingWizard usage below.
	const expandedActions = (
		<>
			<If condition={!isDriverPassenger && (usingExistingPassenger || !passengerTicketIsEmpty(passenger))}>
				<Button
					className="clear-passenger-button"
					onClick={event => {
						event.stopPropagation();
						setExpanded(passenger.id, true);
						onClearForm();
					}}
					colors={Colors.Grey}
					display={Display.Outline}
					newStyles
				>
					Clear
				</Button>
			</If>
			<Button
				className="add-passenger-button"
				onClick={event => {
					event.stopPropagation();
					setExpanded(passenger.id, true);
					setOverrideShowErrors(true);
				}}
				colors={Colors.Primary}
				display={Display.Solid}
				newStyles
			>
				Done
			</Button>
		</>
	);

	return (
		<div
			className={classNames(
				'passenger-detail__container booking-section-card',
				{ 'check-in': forCheckIn },
				{ 'can-click': !expanded },
				{ 'passenger-detail__container--error': showCollapsedErrorState },
			)}
			role="button"
			tabIndex={count}
			onClick={() => {
				if (!expanded) {
					setExpanded(passenger.id, true);
				}
			}}
			onKeyDown={() => {
				if (!expanded) {
					setExpanded(passenger.id, true);
				}
			}}
		>
			<div className="passenger-details__header">
				<span
					className={classNames('passenger-details__status', {
						'passenger-details__status--complete': isComplete === true,
						'passenger-details__status--pending': isComplete === false && !showCollapsedErrorState,
						'passenger-details__status--error': showCollapsedErrorState,
					})}
					aria-label={(() => {
						if (showCollapsedErrorState) return 'Missing or incomplete fields';
						return isComplete ? 'Complete' : 'Pending information';
					})()}
				>
					<Icon
						name={(() => {
							if (showCollapsedErrorState) return 'error';
							return isComplete ? 'check_circle' : 'pending';
						})()}
						useMaterialIcons
					/>
				</span>
				<div className="header__label">
					<h6 className="passenger-details__name">
						<span className="header__label-name">
							{stringIsEmpty(passenger.firstName)
								? `Passenger ${count}`
								: `${passenger.firstName} ${passenger.lastName ?? ''}`}
						</span>
						<If condition={!!passenger.isPrimaryContact && appConfigStore.collectPrimaryBookingNameEnabled}>
							<span className="passenger-details__primary-tag">
								{intl.formatMessage({
									id: 'passenger-tab__primary-contact__tag',
									defaultMessage: 'Primary contact',
								})}
							</span>
						</If>
					</h6>
					<p className="passenger-details__meta">
						{[
							passengerTypeStore.getSingularName(passenger.key as PassengerTypeKey),
							isDriverPassenger ? 'Driver' : null,
						]
							.filter(Boolean)
							.join(' · ')}
					</p>
				</div>
				<div className="passenger-details__header-actions">
					<If condition={!expanded}>
						<Button
							className="edit-passenger__button"
							colors={Colors.Grey}
							display={Display.Outline}
							newStyles
							icon={{ icon: 'edit', iconPos: 'icon-right' }}
						>
							Edit
						</Button>
					</If>
					<If condition={expanded && !isMobileBookingWizard}>{expandedActions}</If>
				</div>
			</div>
			<If condition={expanded}>
				<div className="passenger-details__body grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
					<If condition={appConfigStore.passengerFirstNameEnabled}>
						<TextInput
							id="passenger-first-name"
							label={intl.formatMessage({
								id: 'passenger-tab__first-name__label',
								defaultMessage: 'First name',
							})}
							placeholder={intl.formatMessage({
								id: 'passenger-tab__first-name__placeholder',
								defaultMessage: 'Enter your first name',
							})}
							required={firstNameRequired}
							disabled={driverFirstNameLocked}
							value={passenger.firstName ?? ''}
							onChange={event => handleInputChange('firstName', event.target.value)}
							error={showFieldErrors ? passengersErrors?.firstName : undefined}
						/>
					</If>
					<If condition={appConfigStore.passengerLastNameEnabled}>
						<TextInput
							id="passenger-last-name"
							label={intl.formatMessage({
								id: 'passenger-tab__last-name__label',
								defaultMessage: 'Last name',
							})}
							placeholder={intl.formatMessage({
								id: 'passenger-tab__last-name__placeholder',
								defaultMessage: 'Enter your last name',
							})}
							required={lastNameRequired}
							disabled={driverLastNameLocked}
							value={passenger.lastName ?? ''}
							onChange={event => handleInputChange('lastName', event.target.value)}
							error={showFieldErrors ? passengersErrors?.lastName : undefined}
						/>
					</If>
					<If condition={appConfigStore.passengerPhoneEnabled}>
						<PhoneInput
							id="passenger-phone"
							label={intl.formatMessage({
								id: 'passenger-tab__phone__label',
								defaultMessage: 'Phone number',
							})}
							placeholder={intl.formatMessage({
								id: 'passenger-tab__phone__placeholder',
								defaultMessage: 'Enter your phone number',
							})}
							required={fieldsRequired && appConfigStore.passengerPhoneRequired}
							disabled={driverPhoneLocked}
							value={passenger.phone ?? ''}
							onChange={value => handleInputChange('phone', value)}
							error={showFieldErrors ? passengersErrors?.phone : undefined}
						/>
					</If>
					<If condition={appConfigStore.passengerEmailEnabled}>
						<TextInput
							id="passenger-email"
							label={intl.formatMessage({
								id: 'passenger-tab__email__label',
								defaultMessage: 'Email',
							})}
							placeholder={intl.formatMessage({
								id: 'passenger-tab__email__placeholder',
								defaultMessage: 'Enter your email',
							})}
							required={fieldsRequired && appConfigStore.passengerEmailRequired}
							type="email"
							value={passenger.email ?? ''}
							onChange={event => handleInputChange('email', event.target.value)}
							error={showFieldErrors ? passengersErrors?.email : undefined}
						/>
					</If>
					<If condition={appConfigStore.passengerAgeEnabled}>
						<NumberInput
							id="passenger-age"
							label={intl.formatMessage({
								id: 'passenger-tab__age__label',
								defaultMessage: 'Age',
							})}
							placeholder={intl.formatMessage({
								id: 'passenger-tab__age__placeholder',
								defaultMessage: 'Enter your age',
							})}
							required={fieldsRequired && appConfigStore.passengerAgeRequired}
							allowDecimals={false}
							allowNegative={false}
							value={passenger.age ?? undefined}
							onChange={value => handleInputChange('age', value ?? null)}
							error={showFieldErrors ? passengersErrors?.age : undefined}
						/>
					</If>
					<If condition={appConfigStore.passengerDateOfBirthEnabled}>
						<FlatPickrDateInput
							id="passenger-dateofbirth"
							label={intl.formatMessage({
								id: 'passenger-tab__dateofbirth__label',
								defaultMessage: 'Date of birth',
							})}
							placeholder={intl.formatMessage({
								id: 'passenger-tab__dateofbirth__placeholder',
								defaultMessage: 'Enter your date of birth',
							})}
							required={fieldsRequired && appConfigStore.passengerDateOfBirthRequired}
							value={formatDateValue(dateModel.dateOfBirth)}
							flatpickrOptions={{
								maxDate: new Date(),
								allowInput: true,
								dateFormat: 'd/m/Y',
								locale: {
									firstDayOfWeek: appConfigStore.CalendarStartDay,
									weekdays: {
										shorthand: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
										longhand: ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'],
									},
								},
								disableMobile: true,
							}}
							onChange={isoString => {
								const newDate = new Date(isoString);
								dateModel.dateOfBirth = newDate;
								handleInputChange('dateOfBirth', newDate);
								if (forCheckIn) {
									passenger.dateOfBirth = newDate;
								}
							}}
							error={showFieldErrors ? passengersErrors?.dateOfBirth : undefined}
						/>
					</If>
					<If condition={appConfigStore.passengerAddressEnabled}>
						<TextInput
							id="passenger-address"
							label={intl.formatMessage({
								id: 'passenger-tab__address__label',
								defaultMessage: 'Address',
							})}
							placeholder={intl.formatMessage({
								id: 'passenger-tab__address__placeholder',
								defaultMessage: 'Enter your address',
							})}
							required={fieldsRequired && appConfigStore.passengerAddressRequired}
							value={passenger.address ?? ''}
							onChange={event => handleInputChange('address', event.target.value ?? null)}
							error={showFieldErrors ? passengersErrors?.address : undefined}
						/>
					</If>
					<If condition={appConfigStore.passengerGenderEnabled}>
						<SelectInput
							label={intl.formatMessage({
								id: 'passenger-tab__gender__label',
								defaultMessage: 'Gender',
							})}
							placeholder={intl.formatMessage({
								id: 'passenger-tab__gender__placeholder',
								defaultMessage: 'Select your gender',
							})}
							required={fieldsRequired && appConfigStore.passengerGenderRequired}
							isSearchable={false}
							options={appConfigStore.passengerGenderOptions.map(x => ({ value: x, label: x }))}
							value={passenger.gender ? { value: passenger.gender, label: passenger.gender } : null}
							onChange={(option: any) => handleInputChange('gender', option?.value ?? null)}
							error={showFieldErrors ? passengersErrors?.gender : undefined}
						/>
					</If>
				</div>
				<If condition={isMobileBookingWizard}>
					<div className="passenger-details__footer-actions">{expandedActions}</div>
				</If>
			</If>
		</div>
	);
}
